import { notifications, notificationsStore, type NotificationData } from '@mantine/notifications';
import { toastNotifications as nativeToastNotifications } from '@uag/react-core';
import { t } from 'i18next';

type NotificationKind = 'success' | 'information' | 'warning' | 'error';

interface NotificationGroup {
    count: number;
    id: string;
}

const notificationGroups = new Map<string, NotificationGroup>();
let nextNotificationId = 0;

function showNotification(kind: NotificationKind, notification: NotificationData): void {
    const { title, message } = notification;
    if (typeof message !== 'string' || (title !== undefined && typeof title !== 'string')) {
        showNativeNotification(kind, notification);
        return;
    }

    const key = JSON.stringify([kind, notification.id ?? null, notification.position ?? null, title ?? null, message]);
    const group = notificationGroups.get(key);
    const state = notificationsStore.getState();
    const isActive =
        group !== undefined && [...state.notifications, ...state.queue].some((item) => item.id === group.id);
    const count = isActive ? group.count + 1 : 1;
    const id = isActive ? group.id : (notification.id ?? `repeated-toast-${++nextNotificationId}`);
    const onClose = (closedNotification: NotificationData) => {
        if (notificationGroups.get(key)?.id === id) {
            notificationGroups.delete(key);
        }
        notification.onClose?.(closedNotification);
    };

    const groupedNotification: NotificationData = {
        ...notification,
        id,
        message: count === 1 ? message : t('toastRepeatedCount', { message, count }),
        onClose
    };

    notificationGroups.set(key, { id, count });
    if (isActive) {
        notifications.update(groupedNotification);
    } else {
        showNativeNotification(kind, groupedNotification);
    }
}

function showNativeNotification(kind: NotificationKind, notification: NotificationData): void {
    switch (kind) {
        case 'success':
            nativeToastNotifications.success(notification);
            break;
        case 'information':
            nativeToastNotifications.information(notification);
            break;
        case 'warning':
            nativeToastNotifications.warning(notification);
            break;
        case 'error':
            nativeToastNotifications.error(notification);
            break;
    }
}

export const toastNotifications = {
    loading: (notification: Parameters<typeof nativeToastNotifications.loading>[0]) => {
        nativeToastNotifications.loading(notification);
    },
    hide: (id: string) => {
        for (const [key, group] of notificationGroups) {
            if (group.id === id) {
                notificationGroups.delete(key);
            }
        }
        nativeToastNotifications.hide(id);
    },
    success: (notification: NotificationData) => showNotification('success', notification),
    sucess: (notification: NotificationData) => showNotification('success', notification),
    information: (notification: NotificationData) => showNotification('information', notification),
    warning: (notification: NotificationData) => showNotification('warning', notification),
    error: (notification: NotificationData) => showNotification('error', notification),
    errorDetail: (notification: Parameters<typeof nativeToastNotifications.errorDetail>[0], error: unknown) => {
        nativeToastNotifications.errorDetail(notification, error);
    }
};
